<script setup lang="ts">
import type { NavigationMenuItem } from '@nuxt/ui'
import { IconsToolbarArticles, IconsToolbarCustomerService, IconsToolbarEvents, IconsToolbarLaptop, IconsToolbarPodcasts } from '#components'

const cart = useCartStore()

const auth = useAuthorizeStore()

const topNavItems = computed<NavigationMenuItem[]>(() => [
  {
    'aria-label': auth.loggedIn ? 'Gå til min side' : 'Gå til innlogging',
    'icon': 'fa7-solid:face-smile',
    'to': auth.loggedIn
      ? {
          name: 'minside',
        }
      : {
          name: 'login',
          query: {
            redirectTo: '/minside',
          },
        },
  },
  {
    'aria-label': cart.isEmpty ? 'Gå til handlekurv' : `Gå til handlekurv, det finnes ${cart.count} produkter i handlekurven din`,
    'icon': 'fa7-solid:bag-shopping',
    'to': {
      name: 'handlekurv',
    },
    'badge': cart.count,
  },
])

const sidebarItems = computed<NavigationMenuItem[]>(() => [{
  label: 'Produktkatalog',
  to: { name: 'products' },
  icon: IconsToolbarLaptop,
}, {
  label: 'Arrangementer',
  to: { name: 'events' },
  icon: IconsToolbarEvents,
}, {
  label: 'Artikler',
  to: { name: 'articles' },
  icon: IconsToolbarArticles,
}, {
  label: 'Podkaster',
  icon: IconsToolbarPodcasts,
  to: { name: 'podcasts' },
}, {
  label: 'Kundeservice',
  to: { name: 'kundeservice' },
  icon: IconsToolbarCustomerService,
}, {
  label: 'Om Aschehoug',
  to: { name: 'om-aschehoug' },
}])

const q = ref<string>()

function handleSearch() {
  if (q.value?.length) {
    navigateTo({ name: 'search', query: { q: q.value } })
  }
}
</script>

<template>
  <UHeader
    title="Gå til forsiden"
    mode="slideover"
    :ui="{
      overlay: 'lg:block bg-black/60',
      toggle: 'lg:block',
      content: 'lg:block bg-green-50 ring-transparent',
      body: 'px-legacy-s py-legacy-m sm:p-legacy-m md:pl-legacy-xl',
      header: 'border-green-pop [&_nav]:hidden [&_a]:hidden [&_button]:text-green-20',
    }"
    :toggle="{
      class: 'text-white hover:text-current hover:contrast-75',
      variant: 'link',
      ui: {
        leadingIcon: 'size-[1.85rem] sm:size-9.25',
      },
    }"
  >
    <template #title>
      <IconsToolbarLogo fill="white" class="w-36 sm:w-auto" />
    </template>

    <template #right>
      <nav class="mr-4 flex items-center gap-6 text-white sm:mr-8 sm:gap-10" aria-label="Toppmeny">
        <NuxtLink
          v-for="item in topNavItems"
          :key="item['aria-label']"
          :aria-label="item['aria-label']"
          class="relative"
          :to="item.to"
        >
          <UIcon
            v-if="item.icon"
            :name="item.icon"
            class="relative size-[1.85rem] sm:size-9.25"
          />

          <UBadge
            v-if="item.badge"
            :label="String(item.badge)"
            class="absolute top-1/2 -left-1 flex size-5 items-center justify-center rounded-full bg-yellow-legacy-40 text-xs font-semibold text-black-80 sm:-left-2 sm:size-6"
          />
        </NuxtLink>
      </nav>
    </template>

    <template #body>
      <div class="flex flex-col border-b border-green-20">
        <label for="searchfield" class="text-base-versal text-green-20 sm:text-lg-versal">SØK</label>
        <UiSearchField v-model="q" @submit.prevent="handleSearch" />
      </div>

      <UNavigationMenu
        :items="sidebarItems"
        orientation="vertical"
        :ui="{
          linkLeadingIcon: 'size-8',
          link: 'before:hidden text-white no-underline hover:text-yellow-legacy-40 text-2xl sm:text-sidebar-link gap-x-7 py-6 border-b border-green-20',
        }"
      />
    </template>
  </UHeader>
</template>
